import { Stack } from 'src/components/layout/stack';
import {
  AxiosHookConfig,
  isNetworkError,
  isServerError,
} from 'src/hooks/use-axios';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { ToastContextValue, useToast } from 'src/hooks/use-toasts';
import {
  Forbidden,
  Gone,
  MissingMock,
  NotFound,
  Unauthorized,
} from 'src/utils/errors';
import { getTraceId } from 'src/utils/get-trace-id';
import { isObject } from 'src/utils/is-object';
import { isVerificationProblemWithoutField } from 'src/utils/is-verification-problem-without-field';
import { reportError } from 'src/utils/report-error';
import { useMemoOne } from 'use-memo-one';

export function handleRequestError(
  error: unknown,
  setReauthenticate: (value: boolean) => void
): [string, boolean] {
  if (isNetworkError(error)) {
    // note: we treat every axios error that has no response as a network error, but the
    // reasons why we don't have a response can be different. E.g. we can see
    // code === 'ERR_NETWORK' for CORS/firewall issues/blocked domains or code === 'ECONNABORTED'
    // for timeouts.
    return [
      error.code === 'ECONNABORTED'
        ? `A request took too long and was cancelled.`
        : `Request couldn't be made. Please check your network connectivity and firewall settings.`,
      // if you came to this place to enable logging for CORS keep in mind that
      // network errors happen for a lot of other reasons as well (flaky internet, no VPN, etc.).
      // in that past we got _so many_ false alarms that we decided to completely turn off
      // the reporting of network errors.
      // if you'd like to monitor something like CORS errors, find a different way (e.g. inspect
      // request/response headers on the server side).
      false,
    ];
  } else if (
    (isServerError(error, 400) || isServerError(error, 500)) &&
    error.config.url?.startsWith(PRISMA_CONFIG.oldBackendUrl) &&
    isObject(error.response.data) &&
    ((typeof (error.response.data as any).message === 'string' &&
      (error.response.data as any).message) || // sometimes the message is an empty string, so check description as well
      (typeof (error.response.data as any).description === 'string' &&
        (error.response.data as any).description))
  ) {
    // the old backend sometimes responds with human readable error messages
    // e.g. https://sentry.io/organizations/prisma-european-capacity-platf/issues/2397095793/?project=1832012
    const msg =
      (error.response.data as any).message ||
      (error.response.data as any).description;
    return [
      // special handling for 'NullPointerException', because it is so common
      msg === 'NullPointerException'
        ? `An unknown error happened. We're sorry.`
        : msg,
      error.response.status === 500,
    ];
  } else if (isServerError(error, 401) || error instanceof Unauthorized) {
    setReauthenticate(true);
    return ['Your session has expired. Please login again.', false];
  } else if (isServerError(error, 403) || error instanceof Forbidden) {
    return [`You don’t have permission to do this action.`, false];
  } else if (isServerError(error, 404) || error instanceof NotFound) {
    return [`The requested document could not be found.`, false];
  } else if (isServerError(error, 410) || error instanceof Gone) {
    return [`The requested document is gone.`, false];
  } else if (isVerificationProblemWithoutField(error)) {
    return [error.response.data.message, false];
  } else if (
    isObject(error) &&
    (error as any).name === 'NS_ERROR_FILE_CORRUPTED'
  ) {
    return [`The browser cache is corrupted.`, false];
  } else if (error instanceof MissingMock) {
    return [`This feature is not mocked in Storybook.`, false];
  } else {
    return [`An unknown error happened. We're sorry.`, true];
  }
}

// this is most commonly used for errors that happen *after* the page has loaded,
// so we don't want to show an error page, but just an error notification
export function handleRequestErrorWithNotification({
  notify,
  error,
  setReauthenticate,
}: {
  notify: ToastContextValue;
  error: unknown;
  setReauthenticate: (value: boolean) => void;
}) {
  const [message, report] = handleRequestError(error, setReauthenticate);
  const traceId = isServerError(error) ? getTraceId(error) : undefined;
  notify({
    type: 'error',
    children:
      report && traceId ? (
        <Stack>
          <p>{message}</p>
          <small>(Trace ID: {traceId})</small>
        </Stack>
      ) : (
        message
      ),
  });

  if (report) reportError(error);
}

export function useCustomAxiosConfig() {
  const notify = useToast();
  const [_, setReauthenticate] = useReauthenticate();

  const axiosHookConfig = useMemoOne<AxiosHookConfig>(
    () => ({
      onError(error, meta) {
        // let error boundary handle errors from eager requests: the very first request when flagged as neededOnPageLoad: true
        if (meta.isPageLoadError) throw error;

        handleRequestErrorWithNotification({
          notify,
          error,
          setReauthenticate,
        });
      },
      cache: {},
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return axiosHookConfig;
}
